import { useEffect, useState } from 'react';
import { useSessionTracking } from 'hooks/useSessionTracking';
import AppSettings from 'config/AppSettings';
import getFeatureFlags from 'helpers/getFeatureFlags';
import clsx from 'clsx';
import CookieConsent from 'components/CookieConsent';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import AppRouter from 'routers/AppRouter';
import MobileWarning from 'components/MobileWarning';

import 'normalize.css/normalize.css';
import './assets/styles/styles.scss';
import StorageHandler from 'config/StorageHandler';
import { storageKeys, language } from 'config/settings.json';
import { captureCampaignRegistrationCode } from 'helpers/campaignRegistrationCode';
import { ensureCurrentAppStorage } from 'helpers/ensureCurrentAppStorage';
import { getLangFile, normalizeLangParam, toLangPreference } from 'helpers/languageParam';
import locales from 'config/i18n';

import i18n from 'i18n-react';
import { AppContext } from './AppContext';
import { useSanity } from 'hooks/useSanity';

window.global ||= window;

import './instrument';

export function App() {
  const mobileRegex = new RegExp('Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini', 'i');
  const isMobileDevice = mobileRegex.test(navigator.userAgent);
  const hasMobileInUrl = window.location.href.includes('mobile');
  const onMobile = isMobileDevice && !hasMobileInUrl;

  const [loading, setLoading] = useState(true);
  const [featureFlags, setFeatureFlags] = useState({});

  useSanity();

  useSessionTracking();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    captureCampaignRegistrationCode(window.location.search);

    StorageHandler.exists(storageKeys.lang).then((languageExists) => {
      if (languageExists) {
        if (params.get('lang')) {
          const normalizedCode = getLangFile(params.get('lang'));
          const match = normalizeLangParam(normalizedCode, language, locales);
          if (!match) {
            i18n.setTexts(locales.en);
          } else {
            StorageHandler.save(storageKeys.lang, toLangPreference(match));
            i18n.setTexts(locales[match.localeKey] ?? locales.en);
          }
        } else {
          StorageHandler.load(storageKeys.lang).then((lang) => {
            i18n.setTexts(locales[lang?.langFile] ?? locales.en);
          });
        }
      } else if (params.get('lang')) {
        const normalizedCode = getLangFile(params.get('lang'));
        const match = normalizeLangParam(normalizedCode, language, locales);
        if (!match) {
          i18n.setTexts(locales.en);
        } else {
          StorageHandler.save(storageKeys.lang, toLangPreference(match));
          window.location.reload();
        }
      } else {
        StorageHandler.save(storageKeys.lang, {
          country: 'English',
          code: 'en',
          langFile: 'en',
        });
        i18n.setTexts(locales.en);

        window.location.reload();
      }
    });
  }, []);

  useEffect(() => {
    ensureCurrentAppStorage()
      .then(() => AppSettings())
      .then(async (res) => {
        if (res) {
          const lang = await StorageHandler.load(storageKeys.lang);
          const flags = await getFeatureFlags();
          i18n.setTexts(locales[lang?.langFile] ?? locales.en);
          setFeatureFlags(flags);
          setLoading(false);
        }
      });
  }, []);

  const params = new URLSearchParams(window.location.search);

  const { cookieEnabled } = window.navigator;

  let header = true;
  if (params.get('header') === 'false') {
    header = false;
  }

  if (params.get('mobile') === 'true') {
    header = false;
  }

  if (window.location.pathname === '/register-mobile') {
    header = false;
  }

  if (window.location.pathname === '/register') {
    header = false;
  }

  let mobile = onMobile;
  if (import.meta.env.VITE_ENABLE_PREREGISTRATION === 'true') {
    mobile = false;
  }

  // const setAsked = () => {
  //   // this.setState({ askedBefore: true });
  //   // StorageHandler.save('askedMobile', true);
  //   window.history.back();
  // };

  return (
    <div
      className={clsx('container__router', {
        'featureFlagStyles__headers--bold': featureFlags.titles_to_bold,
        'featureFlagStyles__headers--capitalize': featureFlags.titles_to_capitalize,
        'featureFlagStyles__headers--uppercase': featureFlags.titles_to_uppercase && !featureFlags.titles_to_capitalize,
        [`clientStyles__custom--${import.meta.env.VITE_APP_ID}`]: import.meta.env.VITE_APP_ID,
      })}
    >
      {!cookieEnabled && <CookieConsent />}
      {loading && (
        <div className="appLoader">
          <FontAwesomeIcon icon={['fas', 'circle-notch']} spin />
        </div>
      )}
      {!loading && (
        <AppContext>
          <AppRouter />
          {header && mobile && <MobileWarning disabled={import.meta.env.DEV} />}
        </AppContext>
      )}
    </div>
  );
}
